<template>
    <Head :title="`External Request ${externalRequest.request_number}`" />

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <!-- Header -->
        <div class="flex justify-between items-center mb-6">
            <div>
                <div class="flex items-center gap-3 mb-2">
                    <Link
                        :href="route('external-requests.index')"
                        class="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                    >
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                    </Link>
                    <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">{{ externalRequest.request_number }}</h1>
                    <span :class="getRequestTypeClass(externalRequest.request_type)">
                        {{ externalRequest.request_type_display }}
                    </span>
                    <span :class="getStatusClass(externalRequest.status)">
                        {{ externalRequest.status_display }}
                    </span>
                </div>
                <p class="text-gray-600 dark:text-gray-400">{{ externalRequest.title }}</p>
            </div>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <!-- Main Content -->
            <div class="lg:col-span-2 space-y-6">
                <!-- Request Details Section 1: Basic Info -->
                <div class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                    <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                        <h3 class="text-lg font-medium text-gray-900 dark:text-white">Request Information</h3>
                    </div>
                    <div class="px-6 py-4 space-y-4">
                        <!-- Title -->
                        <div>
                            <label class="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Title</label>
                            <p class="text-base text-gray-900 dark:text-white font-medium">{{ externalRequest.title }}</p>
                        </div>

                        <!-- Type, Priority, Submitted At, IP Address in 2x2 Grid -->
                        <div class="grid grid-cols-2 gap-4">
                            <div>
                                <label class="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Type</label>
                                <span :class="getRequestTypeClass(externalRequest.request_type)">
                                    {{ externalRequest.request_type_display }}
                                </span>
                            </div>
                            <div v-if="externalRequest.priority">
                                <label class="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Priority</label>
                                <span :class="getPriorityClass(externalRequest.priority)">
                                    {{ externalRequest.priority_display }}
                                </span>
                            </div>
                            <div>
                                <label class="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Submitted At</label>
                                <p class="text-sm text-gray-900 dark:text-white">{{ formatDate(externalRequest.created_at) }}</p>
                            </div>
                            <div>
                                <label class="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">IP Address</label>
                                <p class="text-sm text-gray-900 dark:text-white font-mono">{{ externalRequest.ip_address || 'N/A' }}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Request Details Section 2: Description -->
                <div class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                    <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                        <h3 class="text-lg font-medium text-gray-900 dark:text-white">Description</h3>
                    </div>
                    <div class="px-6 py-4">
                        <p class="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{{ externalRequest.description }}</p>
                    </div>
                </div>

                <!-- Converted/Closed Information -->
                <div v-if="externalRequest.status === 'converted'" class="bg-green-50 dark:bg-green-900/20 rounded-lg shadow border border-green-200 dark:border-green-800 p-6">
                    <h3 class="text-lg font-medium text-green-900 dark:text-green-100 mb-2">Converted Successfully</h3>
                    <p class="text-sm text-green-800 dark:text-green-200 mb-4">
                        This request has been converted to {{ externalRequest.converted_type_display }}.
                    </p>
                    <Link
                        v-if="externalRequest.converted_incident"
                        :href="route('incidents.show', externalRequest.converted_id)"
                        class="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700"
                    >
                        Incident {{ externalRequest.converted_incident.incident_number }}
                    </Link>
                    <Link
                        v-if="externalRequest.converted_work_order"
                        :href="route('work-orders.show', externalRequest.converted_id)"
                        class="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700"
                    >
                        Work Order {{ externalRequest.converted_work_order.work_order_number }}
                    </Link>
                    <Link
                        v-if="externalRequest.converted_ticket"
                        :href="route('tickets.show', externalRequest.converted_id)"
                        class="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700"
                    >
                        Ticket {{ externalRequest.converted_ticket.ticket_number }}
                    </Link>
                    <div class="mt-3 text-xs text-green-700 dark:text-green-300">
                        Converted by {{ getUserName(externalRequest.converted_by_user) }} on {{ formatDate(externalRequest.converted_at) }}
                    </div>
                </div>

                <div v-if="externalRequest.status === 'closed_without_action'" class="bg-gray-50 dark:bg-gray-700 rounded-lg shadow border border-gray-200 dark:border-gray-600 p-6">
                    <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-2">Closed Without Action</h3>
                    <div class="space-y-2">
                        <div>
                            <label class="block text-sm font-medium text-gray-500 dark:text-gray-400">Reason</label>
                            <p class="mt-1 text-sm text-gray-900 dark:text-white">{{ externalRequest.closed_reason_display }}</p>
                        </div>
                        <div v-if="externalRequest.closed_notes">
                            <label class="block text-sm font-medium text-gray-500 dark:text-gray-400">Notes</label>
                            <p class="mt-1 text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{{ externalRequest.closed_notes }}</p>
                        </div>
                        <div class="text-xs text-gray-600 dark:text-gray-400">
                            Closed by {{ getUserName(externalRequest.closed_by_user) }} on {{ formatDate(externalRequest.closed_at) }}
                        </div>
                    </div>
                </div>
            </div>

            <!-- Sidebar -->
            <div class="space-y-6">
                <!-- Submitter Information -->
                <div class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                    <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                        <h3 class="text-lg font-medium text-gray-900 dark:text-white">Submitter Information</h3>
                    </div>
                    <div class="px-6 py-4 space-y-3">
                        <div v-if="externalRequest.submitter_name">
                            <label class="block text-sm font-medium text-gray-500 dark:text-gray-400">Name</label>
                            <p class="mt-1 text-sm text-gray-900 dark:text-white">{{ externalRequest.submitter_name }}</p>
                        </div>
                        <div v-if="externalRequest.submitter_email">
                            <label class="block text-sm font-medium text-gray-500 dark:text-gray-400">Email</label>
                            <p class="mt-1 text-sm text-gray-900 dark:text-white">{{ externalRequest.submitter_email }}</p>
                        </div>
                        <div v-if="externalRequest.submitter_phone">
                            <label class="block text-sm font-medium text-gray-500 dark:text-gray-400">Phone</label>
                            <p class="mt-1 text-sm text-gray-900 dark:text-white">{{ externalRequest.submitter_phone }}</p>
                        </div>
                        <div v-if="externalRequest.submitter_user">
                            <div class="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-800">
                                <p class="text-xs text-blue-800 dark:text-blue-200 font-medium">✓ Matched to existing user</p>
                                <p class="text-xs text-blue-700 dark:text-blue-300 mt-1">{{ externalRequest.submitter_user.full_name }}</p>
                            </div>
                        </div>
                        <div v-if="!externalRequest.submitter_name && !externalRequest.submitter_email && !externalRequest.submitter_phone">
                            <p class="text-sm text-gray-500 dark:text-gray-400 italic">Anonymous submission</p>
                        </div>
                    </div>
                </div>

                <!-- Actions -->
                <div v-if="externalRequest.status === 'pending_review'" class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                    <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                        <h3 class="text-lg font-medium text-gray-900 dark:text-white">Actions</h3>
                    </div>
                    <div class="px-6 py-4 space-y-3">
                        <button
                            @click="showConvertToIncidentModal = true"
                            class="w-full inline-flex justify-center items-center px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700"
                        >
                            Convert to Incident
                        </button>
                        <button
                            @click="showConvertToWorkOrderModal = true"
                            class="w-full inline-flex justify-center items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
                        >
                            Convert to Work Order
                        </button>
                        <button
                            @click="showConvertToTicketModal = true"
                            class="w-full inline-flex justify-center items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700"
                        >
                            Convert to Helpdesk Ticket
                        </button>
                        <button
                            @click="showCloseModal = true"
                            class="w-full inline-flex justify-center items-center px-4 py-2 bg-gray-600 text-white text-sm font-medium rounded-md hover:bg-gray-700"
                        >
                            Close Without Action
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Convert to Incident Modal -->
    <Modal :show="showConvertToIncidentModal" @close="showConvertToIncidentModal = false" max-width="2xl">
        <div class="p-6">
            <h2 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Convert to Incident</h2>
            <form @submit.prevent="convertToIncident">
                <div class="space-y-4">
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title *</label>
                        <input v-model="incidentForm.title" type="text" required
                               class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description *</label>
                        <textarea v-model="incidentForm.description" rows="4" required
                                  class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"></textarea>
                    </div>
                    <div class="grid grid-cols-2 gap-4">
                        <div>
                            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type *</label>
                            <select v-model="incidentForm.type_id" required
                                    class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                                <option value="">Select type</option>
                                <option v-for="type in incidentTypes" :key="type.id" :value="type.id">{{ type.name }}</option>
                            </select>
                        </div>
                        <div>
                            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Location *</label>
                            <select v-model="incidentForm.location_id" required
                                    class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                                <option value="">Select location</option>
                                <option v-for="location in locations" :key="location.id" :value="location.id">{{ location.display_name }}</option>
                            </select>
                        </div>
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Reported By {{ hasIdentifiedReporter ? '' : '(Anonymous)' }}
                        </label>
                        <div v-if="hasIdentifiedReporter">
                            <input
                                type="text"
                                :value="getReporterDisplayName()"
                                disabled
                                class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                            >
                            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">Reporter is pre-filled based on the external request submitter.</p>
                        </div>
                        <div v-else class="p-3 bg-gray-50 dark:bg-gray-700 rounded-md border border-gray-200 dark:border-gray-600">
                            <p class="text-sm text-gray-600 dark:text-gray-400">
                                This incident will be recorded as <strong>anonymous</strong> since the submitter was not identified in the system.
                            </p>
                        </div>
                    </div>
                    <div>
                        <label class="flex items-center">
                            <input v-model="incidentForm.is_near_miss" type="checkbox" class="rounded border-gray-300 dark:border-gray-600 text-blue-600">
                            <span class="ml-2 text-sm text-gray-700 dark:text-gray-300">Mark as Near Miss</span>
                        </label>
                    </div>
                </div>
                <div class="mt-6 flex justify-end gap-3">
                    <button type="button" @click="showConvertToIncidentModal = false"
                            class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600">
                        Cancel
                    </button>
                    <button type="submit" :disabled="incidentForm.processing"
                            class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50">
                        Convert to Incident
                    </button>
                </div>
            </form>
        </div>
    </Modal>

    <!-- Convert to Work Order Modal -->
    <Modal :show="showConvertToWorkOrderModal" @close="showConvertToWorkOrderModal = false" max-width="2xl">
        <div class="p-6">
            <h2 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Convert to Work Order</h2>
            <form @submit.prevent="convertToWorkOrder">
                <div class="space-y-4">
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title *</label>
                        <input v-model="workOrderForm.title" type="text" required
                               class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description *</label>
                        <textarea v-model="workOrderForm.description" rows="4" required
                                  class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"></textarea>
                    </div>
                    <div class="grid grid-cols-2 gap-4">
                        <div>
                            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type *</label>
                            <select v-model="workOrderForm.type" required
                                    class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                                <option value="corrective">Corrective</option>
                                <option value="preventive">Preventive</option>
                                <option value="predictive">Predictive</option>
                                <option value="inspection">Inspection</option>
                                <option value="calibration">Calibration</option>
                                <option value="emergency">Emergency</option>
                            </select>
                        </div>
                        <div>
                            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Priority *</label>
                            <select v-model="workOrderForm.priority" required
                                    class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                                <option value="low">Low</option>
                                <option value="normal">Normal</option>
                                <option value="high">High</option>
                                <option value="urgent">Urgent</option>
                            </select>
                        </div>
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Requested By *</label>
                        <select v-model="workOrderForm.requested_by" required
                                class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                            <option value="">Select requester</option>
                            <option v-for="user in users" :key="user.id" :value="user.id">{{ user.name }} ({{ user.email }})</option>
                        </select>
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Location</label>
                        <select v-model="workOrderForm.location_id"
                                class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                            <option value="">Select location</option>
                            <option v-for="location in locations" :key="location.id" :value="location.id">{{ location.display_name }}</option>
                        </select>
                    </div>
                </div>
                <div class="mt-6 flex justify-end gap-3">
                    <button type="button" @click="showConvertToWorkOrderModal = false"
                            class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600">
                        Cancel
                    </button>
                    <button type="submit" :disabled="workOrderForm.processing"
                            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50">
                        Convert to Work Order
                    </button>
                </div>
            </form>
        </div>
    </Modal>

    <!-- Convert to Ticket Modal -->
    <Modal :show="showConvertToTicketModal" @close="showConvertToTicketModal = false" max-width="2xl">
        <div class="p-6">
            <h2 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Convert to Helpdesk Ticket</h2>
            <form @submit.prevent="convertToTicket">
                <div class="space-y-4">
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Subject *</label>
                        <input v-model="ticketForm.subject" type="text" required
                               class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description *</label>
                        <textarea v-model="ticketForm.description" rows="4" required
                                  class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"></textarea>
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Priority *</label>
                        <select v-model="ticketForm.priority" required
                                class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                            <option value="low">Low</option>
                            <option value="normal">Normal</option>
                            <option value="high">High</option>
                            <option value="urgent">Urgent</option>
                        </select>
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Reporter *</label>
                        <select v-model="ticketForm.reporter_id" required
                                class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                            <option value="">Select reporter</option>
                            <option v-for="user in users" :key="user.id" :value="user.id">{{ user.name }} ({{ user.email }})</option>
                        </select>
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
                        <select v-model="ticketForm.category_id"
                                class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                            <option value="">Select category</option>
                            <option v-for="category in ticketCategories" :key="category.id" :value="category.id">{{ category.name }}</option>
                        </select>
                    </div>
                </div>
                <div class="mt-6 flex justify-end gap-3">
                    <button type="button" @click="showConvertToTicketModal = false"
                            class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600">
                        Cancel
                    </button>
                    <button type="submit" :disabled="ticketForm.processing"
                            class="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50">
                        Convert to Ticket
                    </button>
                </div>
            </form>
        </div>
    </Modal>

    <!-- Close Without Action Modal -->
    <Modal :show="showCloseModal" @close="showCloseModal = false">
        <div class="p-6">
            <h2 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Close Without Action</h2>
            <form @submit.prevent="closeWithoutAction">
                <div class="space-y-4">
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Reason *</label>
                        <select v-model="closeForm.closed_reason" required
                                class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                            <option value="">Select reason</option>
                            <option value="duplicate">Duplicate</option>
                            <option value="spam">Spam</option>
                            <option value="incomplete">Incomplete Information</option>
                            <option value="out_of_scope">Out of Scope</option>
                            <option value="resolved_externally">Resolved Externally</option>
                            <option value="other">Other</option>
                        </select>
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Additional Notes</label>
                        <textarea v-model="closeForm.closed_notes" rows="4" maxlength="1000" placeholder="Optional additional explanation..."
                                  class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"></textarea>
                        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">Maximum 1000 characters</p>
                    </div>
                </div>
                <div class="mt-6 flex justify-end gap-3">
                    <button type="button" @click="showCloseModal = false"
                            class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600">
                        Cancel
                    </button>
                    <button type="submit" :disabled="closeForm.processing"
                            class="px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-md hover:bg-gray-700 disabled:opacity-50">
                        Close Request
                    </button>
                </div>
            </form>
        </div>
    </Modal>
</template>

<script setup>
import { ref, computed } from 'vue';
import { Head, Link, router } from '@inertiajs/vue3';
import { useSecureForm } from '@/Composables/useSecureForm';
import { useTimezone } from '@/Composables/useTimezone';
import route from '@/Utils/route';
import Modal from '@/Components/Common/Modal.vue';

const props = defineProps({
    externalRequest: Object,
    users: Array,
    incidentTypes: Array,
    locations: Array,
    ticketCategories: Array,
});

const { formatWith } = useTimezone();

// Modal visibility
const showConvertToIncidentModal = ref(false);
const showConvertToWorkOrderModal = ref(false);
const showConvertToTicketModal = ref(false);
const showCloseModal = ref(false);

// Forms
const incidentForm = useSecureForm({
    title: props.externalRequest.title,
    description: props.externalRequest.description,
    type_id: '',
    location_id: '',
    reported_by: props.externalRequest.submitter_user_id || null,
    is_near_miss: false,
});

const workOrderForm = useSecureForm({
    title: props.externalRequest.title,
    description: props.externalRequest.description,
    type: 'corrective',
    priority: props.externalRequest.priority || 'normal',
    requested_by: props.externalRequest.submitter_user_id || '',
    location_id: '',
    asset_id: null,
    service_id: null,
});

const ticketForm = useSecureForm({
    subject: props.externalRequest.title,
    description: props.externalRequest.description,
    priority: props.externalRequest.priority || 'normal',
    reporter_id: props.externalRequest.submitter_user_id || '',
    category_id: '',
});

const closeForm = useSecureForm({
    closed_reason: '',
    closed_notes: '',
});

// Computed
const hasIdentifiedReporter = computed(() => {
    return props.externalRequest.submitter_user_id !== null;
});

// Methods
const convertToIncident = () => {
    incidentForm.post(route('external-requests.convert-incident', props.externalRequest.id), {
        onSuccess: () => {
            showConvertToIncidentModal.value = false;
        },
    });
};

const convertToWorkOrder = () => {
    workOrderForm.post(route('external-requests.convert-work-order', props.externalRequest.id), {
        onSuccess: () => {
            showConvertToWorkOrderModal.value = false;
        },
    });
};

const convertToTicket = () => {
    ticketForm.post(route('external-requests.convert-ticket', props.externalRequest.id), {
        onSuccess: () => {
            showConvertToTicketModal.value = false;
        },
    });
};

const closeWithoutAction = () => {
    closeForm.post(route('external-requests.close', props.externalRequest.id), {
        onSuccess: () => {
            showCloseModal.value = false;
        },
    });
};

const getRequestTypeClass = (requestType) => {
    const classes = {
        incident: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
        work_order: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
        helpdesk_ticket: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
    };
    return classes[requestType] || '';
};

const getStatusClass = (status) => {
    const classes = {
        pending_review: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
        converted: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
        closed_without_action: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
    };
    return classes[status] || '';
};

const getPriorityClass = (priority) => {
    const classes = {
        low: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
        normal: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
        high: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300',
        urgent: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
    };
    return classes[priority] || classes.normal;
};

const formatDate = (date) => {
    if (!date) return 'N/A';
    return formatWith(date, 'dd MMM yyyy HH:mm');
};

const getUserName = (user) => {
    if (!user) return 'N/A';
    return `${user.first_name} ${user.last_name}`.trim() || 'Unknown User';
};

const getReporterDisplayName = () => {
    if (!hasIdentifiedReporter.value) return '';
    const user = props.users.find(u => u.id === props.externalRequest.submitter_user_id);
    return user ? `${user.name} (${user.email})` : props.externalRequest.submitter_email || 'Unknown';
};
</script>
